import { useQuery } from '@tanstack/react-query';
import {
  meApi,
  studentsApi,
  sessionsApi,
  assignmentsApi,
  invitesApi,
  StudentProfileCreate,
  StudentSettingsUpdate,
  AssignmentCreate,
  AssignmentUpdate,
  InviteCreate,
} from '@/lib/api-client';
import { useMutationWithNotification } from '@/hooks/useMutationWithNotification';
import { parentApi } from '../api/parentApi';

// My Students hooks (using new /api/me/students endpoints)
export function useChildren(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: ['me', 'students'],
    queryFn: () => meApi.getMyStudents(),
    enabled: options?.enabled ?? true,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });
}

export function useChild(studentId: string) {
  return useQuery({
    queryKey: ['me', 'students', studentId],
    queryFn: () => meApi.getStudent(studentId),
    enabled: !!studentId,
  });
}

export function useAddChild() {
  return useMutationWithNotification({
    mutationFn: (data: StudentProfileCreate) => meApi.addStudent(data),
    successMessage: 'Student profile added successfully',
    errorMessage: (e) => e.message || 'Failed to add student profile',
    invalidateKeys: [['me', 'students']],
  });
}

export function useUpdateChild() {
  return useMutationWithNotification({
    mutationFn: ({ studentId, data }: { studentId: string; data: StudentProfileCreate }) =>
      meApi.updateStudent(studentId, data),
    successMessage: 'Student profile updated successfully',
    errorMessage: (e) => e.message || 'Failed to update student profile',
    invalidateKeys: [['me', 'students']],
    onSuccess: (_, variables) => {
      // Also invalidate the specific student query
      // (invalidateKeys handles the list query)
    },
  });
}

export function useDeleteChild() {
  return useMutationWithNotification({
    mutationFn: (studentId: string) => meApi.deleteStudent(studentId),
    successMessage: 'Student unlinked successfully',
    errorMessage: (e) => e.message || 'Failed to unlink student',
    invalidateKeys: [['me', 'students']],
  });
}

export function useUpdateStudentSettings() {
  return useMutationWithNotification({
    mutationFn: ({ studentId, data }: { studentId: string; data: StudentSettingsUpdate }) =>
      studentsApi.updateStudentSettings(studentId, data),
    successMessage: 'Settings updated',
    errorMessage: (e) => e.message || 'Failed to update settings',
    // Invalidate every place a student profile is cached so the new value
    // propagates to Header, ChildCard, ChildDetailPage, etc.
    invalidateKeys: [
      ['me', 'students'],
      ['children'],
      ['admin', 'students', 'detail'],
    ],
  });
}

// Student resource hooks
export function useChildProgress(studentId: string) {
  return useQuery({
    queryKey: ['students', studentId, 'progress'],
    queryFn: () => studentsApi.getStudentProgress(studentId),
    enabled: !!studentId,
  });
}

export function useChildActivity(studentId: string, limit: number = 20, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: ['students', studentId, 'activity', limit],
    queryFn: () => studentsApi.getStudentActivity(studentId, limit),
    enabled: !!studentId && (options?.enabled ?? true),
  });
}

export function useActivityDetail(studentId: string, itemType: string, itemId: string) {
  return useQuery({
    queryKey: ['students', studentId, 'activity', itemType, itemId],
    queryFn: () => studentsApi.getActivityDetail(studentId, itemType, itemId),
    enabled: !!studentId && !!itemType && !!itemId,
  });
}

export function useSessionDetail(sessionId: string) {
  return useQuery({
    queryKey: ['session', sessionId],
    queryFn: () => sessionsApi.getSessionDetail(sessionId),
    enabled: !!sessionId,
  });
}

// Assignment hooks
export function useAssignments(studentId?: string, includeCompleted?: boolean) {
  return useQuery({
    queryKey: ['assignments', includeCompleted, studentId],
    queryFn: () => assignmentsApi.getAssignments(includeCompleted ?? false, studentId),
    staleTime: 2 * 60 * 1000,
    gcTime: 5 * 60 * 1000,
  });
}

export function useChildAssignments(studentId: string, includeCompleted: boolean = false, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: ['assignments', includeCompleted, studentId],
    queryFn: () => assignmentsApi.getAssignments(includeCompleted, studentId),
    enabled: !!studentId && (options?.enabled ?? true),
  });
}

export function useAssignment(assignmentId: string) {
  return useQuery({
    queryKey: ['assignment', assignmentId],
    queryFn: () => assignmentsApi.getAssignment(assignmentId),
    enabled: !!assignmentId,
  });
}

export interface AssignmentCreateContext {
  data: AssignmentCreate;
  itemName?: string;
  studentName?: string;
}

export function useCreateAssignment() {
  return useMutationWithNotification({
    mutationFn: (context: AssignmentCreateContext) => assignmentsApi.createAssignment(context.data),
    successMessage: (_, context) => {
      if (context.itemName && context.studentName) return `Assigned "${context.itemName}" to ${context.studentName}`;
      if (context.itemName) return `Assigned "${context.itemName}"`;
      if (context.studentName) return `Assignment created for ${context.studentName}`;
      return 'Assignment created successfully';
    },
    errorMessage: (e) => e.message || 'Failed to create assignment',
    invalidateKeys: [['assignments'], ['parent', 'child']],
  });
}

export function useUpdateAssignment() {
  return useMutationWithNotification({
    mutationFn: ({ assignmentId, data }: { assignmentId: string; data: AssignmentUpdate }) =>
      assignmentsApi.updateAssignment(assignmentId, data),
    successMessage: 'Assignment updated successfully',
    errorMessage: (e) => e.message || 'Failed to update assignment',
    invalidateKeys: [['assignments']],
  });
}

export function useCompleteAssignmentAsParent() {
  return useMutationWithNotification({
    mutationFn: (assignmentId: string) =>
      assignmentsApi.updateAssignment(assignmentId, {
        status: 'completed',
        metadata: { completed_by: 'parent' },
      }),
    successMessage: 'Assignment marked as completed',
    errorMessage: (e) => e.message || 'Failed to complete assignment',
    invalidateKeys: [['assignments']],
  });
}

export function useDeleteAssignment() {
  return useMutationWithNotification({
    mutationFn: (assignmentId: string) => assignmentsApi.deleteAssignment(assignmentId),
    successMessage: 'Assignment deleted successfully',
    errorMessage: (e) => e.message || 'Failed to delete assignment',
    invalidateKeys: [['assignments']],
  });
}

// Child flashcard deck hooks
export function useChildFlashcardDecks(
  studentId: string,
  params?: { deck_type?: string; source?: string },
) {
  return useQuery({
    queryKey: ['students', studentId, 'flashcard-decks', params],
    queryFn: () => studentsApi.getStudentFlashcardDecks(studentId, params),
    enabled: !!studentId,
  });
}

export function useUpdateChildDeck() {
  return useMutationWithNotification({
    mutationFn: ({
      studentId,
      deckId,
      data,
    }: {
      studentId: string;
      deckId: string;
      data: { title?: string; description?: string };
    }) => studentsApi.updateStudentFlashcardDeck(studentId, deckId, data),
    successMessage: 'Deck updated successfully',
    errorMessage: (e) => e.message || 'Failed to update deck',
    invalidateKeys: [['students']],
  });
}

// Invite hooks
export function useInvites(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: ['me', 'invites'],
    queryFn: () => meApi.getInvites(),
    enabled: options?.enabled ?? true,
  });
}

export function useCreateInvite() {
  return useMutationWithNotification({
    mutationFn: (data: InviteCreate = {}) => meApi.createInvite(data),
    successMessage: 'Invite code created successfully',
    errorMessage: (e) => e.message || 'Failed to create invite code',
    invalidateKeys: [['me', 'invites']],
  });
}

export function useAcceptInvite() {
  return useMutationWithNotification({
    mutationFn: (inviteCode: string) => invitesApi.acceptInvite(inviteCode),
    successMessage: (data) => `Successfully linked to parent ${data.parent_username}`,
    errorMessage: (e) => e.message || 'Failed to accept invite',
  });
}

export function useRecommendAssignments() {
  return useMutationWithNotification({
    mutationFn: (childId: string) => assignmentsApi.recommendAssignments(childId),
    successMessage: 'Assignments are being generated and will appear shortly',
    errorMessage: (e) => e.message || 'Failed to generate assignments',
    invalidateKeys: [['assignments']],
  });
}

export function useRecentlyCompletedAssignments() {
  return useQuery({
    queryKey: ['assignments', 'recently-completed'],
    queryFn: () => assignmentsApi.getRecentlyCompletedAssignments(),
    staleTime: 1 * 60 * 1000,
    gcTime: 5 * 60 * 1000,
    refetchInterval: 2 * 60 * 1000,
  });
}

export function useChildRecommendations(childId: string, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: ['recommendations', 'child', childId],
    queryFn: () => parentApi.getChildRecommendations(childId),
    enabled: !!childId && (options?.enabled ?? true),
    staleTime: 5 * 60 * 1000,
  });
}
